import { useState, type CSSProperties } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Icon, type IconName } from "@/components/ui/Icon";
import { IconButton } from "@/components/ui/Button";
import { BRAND } from "@/lib/tokens";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { resolveWorkspaceColor } from "@/lib/colors";
import { RauteMark } from "@/components/ui/RauteMark";
import { compareBoardOrder } from "@/lib/boardOrder";
import { usePinnedBoards } from "@/hooks/usePinnedBoards";
import { useMyBoards } from "@/hooks/useMyBoards";
import { useUnreadSubmissionsCount } from "@/hooks/useUnreadSubmissionsCount";
import { useIdeaTargets } from "@/hooks/useIdeaTargets";
import { t } from "@/lib/strings";
import type { Board, UUID } from "@/lib/types";

const PRIMARY: { to: string; icon: IconName; label: string }[] = [
  { to: "/", icon: "inbox", label: t.navHome },
  { to: "/postfach", icon: "archive", label: t.navPostfach },
  { to: "/meetings", icon: "notebook-text", label: t.navMeetings },
];

function railItemStyle(active: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    height: 32,
    padding: "0 10px",
    borderRadius: 7,
    border: "none",
    background: active ? "var(--surface)" : "transparent",
    color: active ? "var(--ink-1)" : "var(--ink-2)",
    fontSize: 13,
    fontWeight: active ? 500 : 400,
    textAlign: "left" as const,
    fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
    cursor: "pointer",
    boxShadow: active ? "0 1px 0 rgba(13,62,99,0.04), 0 1px 2px rgba(13,62,99,0.04)" : "none",
    transition: "background .12s",
    textDecoration: "none",
  };
}

const railSectionLbl: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  padding: "14px 10px 6px",
};

// Collapsible per-workspace group header (chevron + colour dot + name + count).
const wsHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  width: "100%",
  padding: "13px 10px 5px",
  marginTop: 2,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: 10.5,
  fontWeight: 600,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

/**
 * Being assigned a task must never mint a container in your sidebar.
 *
 * Assigning a person to a row's person-cell on a Protokoll's Aufgaben board mints a
 * ROW-SCOPED guest grant: they see that ONE row, not the board, not the workspace, not
 * the Protokoll. Correct — but a guest-granted board lands in "Geteilt & Gast" with a
 * Gast chip, so one to-do per meeting series drops a near-empty GHOST board into the
 * assignee's rail, growing linearly with the series they are pulled into.
 *
 * The task belongs in a personal aggregation, not a container: Home already lists an
 * assignee's open rows by pure person-cell containment and needs no board access to LIST
 * them. So we drop the board from the rail.
 *
 * 🔴 Strictly `is_aufgaben`-scoped. "Hide guest boards" would strip a TICKET SUBMITTER's
 * board from their rail — a different population, whose guest board is the only way they
 * reach their own ticket.
 *
 * 🔴 Display-only. The guest grant STAYS: opening the row (`GET /boards/{id}` →
 * `require_board_read`) and the `row.assigned` bell deep-link both need it. Revoke it and
 * the row lists on Home and then 403s on click.
 */
function isGhostAufgabenBoard(board: Board): boolean {
  return board.access === "guest" && board.is_aufgaben === true;
}

export function LeftRail() {
  const location = useLocation();
  const path = location.pathname;
  const showDesignSystem = import.meta.env.DEV;
  const wsQuery = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
  });
  const workspaces = wsQuery.data ?? [];
  const pinned = usePinnedBoards();
  // Both board seams are filtered: the rail composes TWO independent sources, and
  // `GET /me/pinned-boards` carries `is_aufgaben` for exactly this reason. Filtering only
  // `useMyBoards()` would leave a pinned ghost board alive in "Angeheftet".
  const pinnedBoards = (pinned.data ?? []).filter((b) => !isGhostAufgabenBoard(b));
  const unread = useUnreadSubmissionsCount();
  // Gate the "Ideenbox" nav item on a published Ideenbox — same rule as the Home
  // "Idee einreichen" card (home_listed), so it stays behind the admin publish toggle.
  const { data: ideaTargets = [] } = useIdeaTargets();
  // Sidebar lists ALL boards the user can reach (workspace switching lives in
  // the Topbar now). Pinned boards already show in their own section above.
  const boardsQuery = useMyBoards(200);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem("flow.rail.collapsed");
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  function toggleWs(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem("flow.rail.collapsed", JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  const pinnedIds = new Set(pinnedBoards.map((b) => b.id));
  const wsColorById = new Map(workspaces.map((w) => [w.id, resolveWorkspaceColor(w)]));
  const memberWsIds = new Set(workspaces.map((w) => w.id));
  const byName = (a: Board, b: Board) => a.name.localeCompare(b.name, "de");
  // Group the user's OWN boards under their workspace; shared/guest boards (other
  // teams) get their own section so nothing mixes across workspaces.
  const ownByWs = new Map<string, Board[]>();
  const sharedGuest: Board[] = [];
  for (const b of boardsQuery.data ?? []) {
    if (isGhostAufgabenBoard(b)) continue;
    if (pinnedIds.has(b.id)) continue;
    if (b.access !== "shared" && b.access !== "guest" && memberWsIds.has(b.workspace_id)) {
      const arr = ownByWs.get(b.workspace_id);
      if (arr) arr.push(b);
      else ownByWs.set(b.workspace_id, [b]);
    } else {
      sharedGuest.push(b);
    }
  }
  const sortedWorkspaces = [...workspaces].sort((a, b) => a.name.localeCompare(b.name, "de"));

  return (
    <aside
      style={{
        width: 232,
        flexShrink: 0,
        padding: "12px 8px",
        borderRight: "1px solid var(--hairline)",
        background: "var(--rail-bg)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        // The rail is its own scroll container (mirrors the <main> fix): on a
        // short screen the workspaces past the fold scroll WITHIN the rail
        // instead of spilling out below its background column.
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      {PRIMARY.map((it) => {
        const active =
          (it.to === "/" && path === "/") ||
          (it.to !== "/" && (path === it.to || path.startsWith(`${it.to}/`)));
        const badge = it.to === "/postfach" && unread > 0 ? unread : 0;
        return (
          <Link key={it.to} to={it.to} style={railItemStyle(active)}>
            <Icon name={it.icon} size={15} color={active ? "var(--accent)" : "var(--ink-3)"} />
            <span style={{ flex: 1 }}>{it.label}</span>
            {badge > 0 ? <UnreadBadge count={badge} /> : null}
          </Link>
        );
      })}
      {ideaTargets.length > 0 ? (
        <Link to="/ideen" style={railItemStyle(path === "/ideen")}>
          <Icon
            name="lightbulb"
            size={15}
            color={path === "/ideen" ? "var(--accent)" : "var(--ink-3)"}
          />
          <span>{t.navIdeen}</span>
        </Link>
      ) : null}
      {showDesignSystem ? (
        <Link to="/system" style={railItemStyle(path === "/system")}>
          <Icon
            name="sliders"
            size={15}
            color={path === "/system" ? "var(--accent)" : "var(--ink-3)"}
          />
          <span>{t.navDesignSystem}</span>
        </Link>
      ) : null}

      {pinnedBoards.length > 0 ? (
        <>
          <div style={railSectionLbl}>{t.navPinned}</div>
          {pinnedBoards.map((board) => (
            <PinnedRow
              key={board.id}
              board={board}
              active={path === `/b/${board.id}`}
              onUnpin={() => pinned.unpin.mutate({ boardId: board.id })}
            />
          ))}
        </>
      ) : null}

      {boardsQuery.isLoading ? (
        <div style={{ ...railItemStyle(false), opacity: 0.5 }}>
          <span>{t.loading}</span>
        </div>
      ) : null}

      {sortedWorkspaces.map((ws) => {
        const boards = (ownByWs.get(ws.id) ?? []).slice().sort(compareBoardOrder);
        if (boards.length === 0) return null;
        const isCollapsed = collapsed.has(ws.id);
        return (
          <div key={ws.id}>
            <button type="button" onClick={() => toggleWs(ws.id)} style={wsHeaderStyle}>
              <Icon name={isCollapsed ? "chevRight" : "chevDown"} size={12} color="var(--ink-4)" />
              <RauteMark color={wsColorById.get(ws.id) ?? "var(--ink-4)"} />
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                }}
              >
                {ws.name}
              </span>
              <span style={{ fontSize: 10.5, color: "var(--ink-4)", fontWeight: 400 }}>
                {boards.length}
              </span>
            </button>
            {!isCollapsed
              ? boards.map((board) => (
                  <BoardRow
                    key={board.id}
                    board={board}
                    active={path === `/b/${board.id}`}
                    wsColor={wsColorById.get(board.workspace_id) ?? "var(--ink-4)"}
                  />
                ))
              : null}
          </div>
        );
      })}

      {sharedGuest.length > 0 ? (
        <>
          <div style={railSectionLbl}>{t.navSharedGuest}</div>
          {sharedGuest.sort(byName).map((board) => (
            <BoardRow
              key={board.id}
              board={board}
              active={path === `/b/${board.id}`}
              wsColor={wsColorById.get(board.workspace_id) ?? "var(--ink-4)"}
            />
          ))}
        </>
      ) : null}

      <div style={{ flex: 1 }}></div>

      <Link to="/settings" style={railItemStyle(path === "/settings")}>
        <Icon
          name="settings"
          size={15}
          color={path === "/settings" ? "var(--accent)" : "var(--ink-3)"}
        />
        <span>{t.navSettings}</span>
      </Link>
    </aside>
  );
}

function BoardRow({ board, active, wsColor }: { board: Board; active: boolean; wsColor: string }) {
  return (
    <Link to="/b/$boardId" params={{ boardId: board.id as UUID }} style={railItemStyle(active)}>
      <RauteMark color={wsColor} />
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {board.name}
      </span>
      {board.access === "guest" || board.access === "shared" ? (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 600,
            color: "var(--ink-4)",
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            padding: "0 4px",
            flexShrink: 0,
          }}
        >
          {board.access === "guest" ? t.guestBadge : t.sharedBadge}
        </span>
      ) : null}
    </Link>
  );
}

function UnreadBadge({ count }: { count: number }) {
  return (
    <span
      style={{
        minWidth: 18,
        height: 18,
        padding: "0 5px",
        borderRadius: 999,
        background: BRAND.orange,
        color: "#fff",
        fontSize: 10.5,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function PinnedRow({
  board,
  active,
  onUnpin,
}: {
  board: Board;
  active: boolean;
  onUnpin: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "flex" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Link
        to="/b/$boardId"
        params={{ boardId: board.id as UUID }}
        style={{
          ...railItemStyle(active),
          flex: 1,
          paddingRight: hover ? 30 : 10,
        }}
      >
        <Icon name="pin" size={14} color={active ? "var(--accent)" : "var(--ink-3)"} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {board.name}
        </span>
      </Link>
      {hover ? (
        <IconButton
          icon="x"
          size={22}
          title={t.unpinBoardAria}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onUnpin();
          }}
          style={{ position: "absolute", right: 4, top: 5 }}
        />
      ) : null}
    </div>
  );
}
